"use client";

import { Text, SectionCard, CardTitle, CardDescription, Section } from "@/src/components";

interface SpacingDemoProps {
  name: string;
  token: string;
  value: string;
  pixels: string;
  usage?: string;
}

function SpacingSwatch({ name, token, value, pixels, usage }: SpacingDemoProps) {
  const width = parseInt(pixels);

  return (
    <div className="flex items-center gap-4 py-3 border-b last:border-b-0">
      <div className="w-12 text-center">
        <Text className="font-semibold">{name}</Text>
      </div>
      <div
        className="h-6 bg-primary rounded"
        style={{ width: `${Math.max(width, 4)}px` }}
      />
      <div className="w-24 text-sm text-muted-foreground font-mono">
        {value}
      </div>
      <div className="w-16 text-sm text-muted-foreground">
        {pixels}
      </div>
      <code className="text-xs bg-muted px-2 py-1 rounded">{token}</code>
      {usage && (
        <span className="text-xs text-muted-foreground ml-auto">{usage}</span>
      )}
    </div>
  );
}

const paddingScale = [
  { name: "1", token: "--p-1", value: "0.125rem", pixels: "2px", usage: "badge-y, micro" },
  { name: "2", token: "--p-2", value: "0.25rem", pixels: "4px", usage: "tight gaps, icon buttons" },
  { name: "3", token: "--p-3", value: "0.5rem", pixels: "8px", usage: "compact (py-2, table cells)" },
  { name: "4", token: "--p-4", value: "0.75rem", pixels: "12px", usage: "medium (p-3, sidebar items)" },
  { name: "5", token: "--p-5", value: "1rem", pixels: "16px", usage: "default (p-4, card content)" },
  { name: "6", token: "--p-6", value: "1.5rem", pixels: "24px", usage: "comfortable (p-6, page containers)" },
  { name: "7", token: "--p-7", value: "2rem", pixels: "32px", usage: "spacious (p-8, large sections)" },
  { name: "8", token: "--p-8", value: "3rem", pixels: "48px", usage: "sections, page margins" },
];

const gapScale = [
  { name: "1", token: "--gap-1", value: "0.25rem", pixels: "4px", usage: "tight" },
  { name: "2", token: "--gap-2", value: "0.5rem", pixels: "8px", usage: "compact" },
  { name: "3", token: "--gap-3", value: "0.75rem", pixels: "12px", usage: "medium" },
  { name: "4", token: "--gap-4", value: "1rem", pixels: "16px", usage: "default" },
  { name: "5", token: "--gap-5", value: "1.5rem", pixels: "24px", usage: "comfortable" },
  { name: "6", token: "--gap-6", value: "2rem", pixels: "32px", usage: "spacious" },
];

const radiusTokens = [
  { name: "sm", token: "--theme-radius-sm", value: "0.375rem", pixels: "6px" },
  { name: "md", token: "--theme-radius-md", value: "0.5rem", pixels: "8px" },
  { name: "lg", token: "--theme-radius-lg", value: "0.75rem", pixels: "12px" },
  { name: "xl", token: "--theme-radius-xl", value: "1rem", pixels: "16px" },
  { name: "full", token: "--theme-radius-full", value: "9999px", pixels: "pill" },
];

export default function DocsTokensSpacing() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Spacing Tokens
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A granular spacing scale with separate tokens for padding and gaps.
            The 8-level padding scale and 6-level gap scale provide flexibility
            for different UI contexts while maintaining consistency.
          </Text>
        </div>
      </Section>

      {/* Padding Scale */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Padding Scale (8 levels)</CardTitle>
              <CardDescription>
                For padding, margins, and directional spacing. Uses asymmetric patterns (px &gt; py typically).
              </CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="text-xs text-muted-foreground mb-4 font-mono">
            Pattern: px-6 py-4 (page) → px-4 py-3 (sidebar) → px-3 py-2 (cards) → px-2 py-0.5 (badges)
          </div>
          {paddingScale.map((spacing) => (
            <SpacingSwatch key={spacing.token} {...spacing} />
          ))}
        </SectionCard>
      </Section>

      {/* Gap Scale */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Gap Scale (6 levels)</CardTitle>
              <CardDescription>
                Separate scale for flex/grid gaps. Simpler than padding as gaps are typically uniform.
              </CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          {gapScale.map((spacing) => (
            <SpacingSwatch key={spacing.token} {...spacing} />
          ))}
        </SectionCard>
      </Section>

      {/* Theme Utilities */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Theme Utilities</CardTitle>
              <CardDescription>Themeable utility classes that reference CSS variables</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Text className="font-semibold">Padding</Text>
              <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
                <code className="block">.p-theme-1 ... .p-theme-8</code>
                <code className="block">.px-theme-1 ... .px-theme-8</code>
                <code className="block">.py-theme-1 ... .py-theme-8</code>
                <code className="block">.pt-theme-*, .pb-theme-*</code>
                <code className="block">.pl-theme-*, .pr-theme-*</code>
              </div>
            </div>

            <div className="space-y-3">
              <Text className="font-semibold">Gap</Text>
              <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
                <code className="block">.gap-theme-1 ... .gap-theme-6</code>
                <code className="block">.gap-x-theme-1 ... .gap-x-theme-6</code>
                <code className="block">.gap-y-theme-1 ... .gap-y-theme-6</code>
              </div>
            </div>

            <div className="space-y-3">
              <Text className="font-semibold">Margin</Text>
              <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
                <code className="block">.m-theme-1 ... .m-theme-6</code>
                <code className="block">.mt-theme-*, .mb-theme-*</code>
                <code className="block">.ml-theme-*, .mr-theme-*</code>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Visual Demo */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Visual Demo</CardTitle>
              <CardDescription>How spacing values look in practice</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="space-y-8">
            <div>
              <Text className="text-sm font-medium mb-3">Gap between items:</Text>
              <div className="flex flex-wrap gap-6">
                {gapScale.slice(0, 4).map((s) => (
                  <div key={s.name} className="text-center">
                    <div className="flex bg-muted rounded p-2" style={{ gap: s.value }}>
                      <div className="w-6 h-6 bg-primary rounded" />
                      <div className="w-6 h-6 bg-primary rounded" />
                      <div className="w-6 h-6 bg-primary rounded" />
                    </div>
                    <Text className="text-xs text-muted-foreground mt-2">gap-theme-{s.name}</Text>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Text className="text-sm font-medium mb-3">Padding around content:</Text>
              <div className="flex flex-wrap gap-6">
                {[paddingScale[2], paddingScale[4], paddingScale[5], paddingScale[6]].map((s) => (
                  <div key={s.name} className="text-center">
                    <div className="bg-muted rounded inline-block" style={{ padding: s.value }}>
                      <div className="w-8 h-8 bg-primary rounded" />
                    </div>
                    <Text className="text-xs text-muted-foreground mt-2">p-theme-{s.name}</Text>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Text className="text-sm font-medium mb-3">Asymmetric padding (common pattern):</Text>
              <div className="flex flex-wrap gap-6">
                <div className="text-center">
                  <div className="bg-muted rounded inline-block px-6 py-4">
                    <div className="w-16 h-8 bg-primary rounded flex items-center justify-center text-xs text-primary-foreground">Page</div>
                  </div>
                  <Text className="text-xs text-muted-foreground mt-2">px-6 py-4</Text>
                </div>
                <div className="text-center">
                  <div className="bg-muted rounded inline-block px-4 py-3">
                    <div className="w-16 h-8 bg-primary rounded flex items-center justify-center text-xs text-primary-foreground">Sidebar</div>
                  </div>
                  <Text className="text-xs text-muted-foreground mt-2">px-4 py-3</Text>
                </div>
                <div className="text-center">
                  <div className="bg-muted rounded inline-block px-3 py-2">
                    <div className="w-16 h-8 bg-primary rounded flex items-center justify-center text-xs text-primary-foreground">Card</div>
                  </div>
                  <Text className="text-xs text-muted-foreground mt-2">px-3 py-2</Text>
                </div>
                <div className="text-center">
                  <div className="bg-muted rounded inline-block px-2 py-0.5">
                    <div className="bg-primary rounded flex items-center justify-center text-xs text-primary-foreground px-1">Badge</div>
                  </div>
                  <Text className="text-xs text-muted-foreground mt-2">px-2 py-0.5</Text>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Border Radius */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Border Radius</CardTitle>
              <CardDescription>Theme-aware corner radius tokens</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex items-center gap-8 flex-wrap">
            {radiusTokens.map((radius) => (
              <div key={radius.name} className="text-center">
                <div
                  className="w-16 h-16 bg-primary"
                  style={{ borderRadius: radius.value }}
                />
                <div className="flex flex-col gap-1">
                  <Text className="text-sm font-medium mt-2">{radius.name}</Text>
                  <Text className="text-xs text-muted-foreground">{radius.value}</Text>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-muted/50 rounded-lg p-4 font-mono text-xs">
            <code>.rounded-theme-sm, .rounded-theme-md, .rounded-theme-lg, .rounded-theme-xl, .rounded-theme-full</code>
          </div>
        </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
          header={<CardTitle>Usage</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <div className="space-y-3">
            <Text className="font-semibold">In Components (styleDrivers):</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block text-muted-foreground">// Card header</code>
              <code className="block">{"header: \"py-theme-3 px-theme-5 border-b-theme\""}</code>
              <code className="block text-muted-foreground">// Sidebar item</code>
              <code className="block">{"itemRow: \"px-theme-3 py-theme-2 gap-theme-2\""}</code>
            </div>
          </div>

          <div className="space-y-3">
            <Text className="font-semibold">In CSS:</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block">{".element {"}</code>
              <code className="block pl-4">{"padding: var(--p-5);"}</code>
              <code className="block pl-4">{"gap: var(--gap-3);"}</code>
              <code className="block pl-4">{"border-radius: var(--theme-radius-lg);"}</code>
              <code className="block">{"}"}</code>
            </div>
          </div>

          <div className="space-y-3">
            <Text className="font-semibold">Theming:</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block text-muted-foreground">/* Override in theme class */</code>
              <code className="block">{".theme-compact {"}</code>
              <code className="block pl-4">{"--p-5: 0.5rem;  /* tighter */"}</code>
              <code className="block pl-4">{"--gap-4: 0.5rem;"}</code>
              <code className="block">{"}"}</code>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SectionCard
            header={<CardTitle className="text-success">Do</CardTitle>}
            contentClassName="p-6"
          >
            <ul className="space-y-2 text-sm">
              <li>✓ Use theme utilities for themeable spacing</li>
              <li>✓ Use asymmetric padding (px {">"} py) for horizontal content</li>
              <li>✓ Use separate gap tokens for flex/grid gaps</li>
              <li>✓ Follow hierarchical pattern (page → sidebar → card → badge)</li>
              <li>✓ Override tokens in theme classes for consistent changes</li>
            </ul>
          </SectionCard>

          <SectionCard
            header={<CardTitle className="text-destructive">Don't</CardTitle>}
            contentClassName="p-6"
          >
            <ul className="space-y-2 text-sm">
              <li>✗ Don't use arbitrary pixel values</li>
              <li>✗ Don't mix themed and non-themed utilities inconsistently</li>
              <li>✗ Don't use padding tokens for gaps (use gap-theme-*)</li>
              <li>✗ Don't skip the token system for one-off spacing</li>
              <li>✗ Don't hardcode spacing in component styles</li>
            </ul>
          </SectionCard>
        </div>
      </Section>
    </>
  );
}
